import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';

import { config } from 'src/config';
import { client as apolloClient } from 'src/shared/graphql';
import { FileStorageScopes } from 'src/shared/graphql/generated';
import { mutations } from 'src/shared/graphql/requests';
import {
  configureFirebaseSentrySync,
  withFirebaseErrorSentry,
  withFirebaseWarningSentry,
} from 'src/shared/telemetry/sentry';
import { configureFirebaseRetry } from 'src/shared/utils/firebase';

const FIREBASE_CONFIG = {
  apiKey: config.FIREBASE_API_KEY,
  authDomain: config.FIREBASE_AUTH_DOMAIN,
  projectId: config.FIREBASE_PROJECT_ID,
  storageBucket: config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: config.FIREBASE_MESSAGING_SENDER_ID,
  appId: config.FIREBASE_APP_ID,
  measurementId: config.FIREBASE_MEASUREMENT_ID,
};

interface FirebaseClientType {
  getApp: () => firebase.app.App;
  teardownApp: () => void;
  authenticate: (
    authToken: string | undefined | null,
    jobId?: number,
    authorId?: number,
  ) => Promise<string | undefined>;
  getNowTimestamp: () => firebase.firestore.Timestamp;
}

const client = (): FirebaseClientType => {
  let app: firebase.app.App | undefined;

  const withFirebaseErrorSentrySync = configureFirebaseSentrySync('error');

  const getApp = (): firebase.app.App => {
    if (typeof app === 'undefined') {
      app = withFirebaseErrorSentrySync(() =>
        firebase.initializeApp(FIREBASE_CONFIG),
      )();
      return app;
    }

    return app;
  };

  const teardownApp = async () => {
    if (app) {
      const firebaseAuth = firebase.auth(app);
      await withFirebaseWarningSentry(() => firebaseAuth.signOut())();

      await withFirebaseWarningSentry(() => {
        if (typeof app !== 'undefined') {
          return app.delete();
        }

        return Promise.resolve();
      })();

      app = undefined;
    }
  };

  /** An auth token used to include with firestore requests and queries versions */
  const authenticate = async (
    authToken: string | undefined | null,
    jobId?: number,
    authorId?: number,
  ) => {
    // TODO - Authenticate should be idempotent for a given [app, authToken, jobId, authorId] tuple
    let token = authToken;
    if (!token) {
      const storageTokenResponse = await mutations.createStorageToken(
        apolloClient,
        {
          storageScope: FileStorageScopes.DesignStudio,
          jobId: jobId?.toString(),
          authorId: authorId?.toString(),
        },
      );

      token = storageTokenResponse?.data?.createFileStorageToken?.token;
    }

    const firebaseApp = getApp();

    // TODO - We should expect a token to exist at this point. The current
    //  unit test suite is not compatible with this requirement.
    // invariant(token, 'Missing storage token');

    if (token) {
      const { EMULATOR_FIREBASE_AUTH: emulatorHost } = config;
      const auth = firebase.auth(firebaseApp);
      if (emulatorHost) {
        auth.useEmulator(emulatorHost);
      }

      const composedSignIn = withFirebaseErrorSentry(
        configureFirebaseRetry()(() => auth.signInWithCustomToken(token ?? '')),
      );

      await composedSignIn();
    }

    return token;
  };

  const getNowTimestamp = () => firebase.firestore.Timestamp.now();

  return {
    getApp,
    teardownApp,
    authenticate,
    getNowTimestamp,
  };
};

export const FirebaseClient = client();
